import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportType, SignalResolution } from '@repo/shared/schema';
import { UserRole } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { toast } from 'sonner';
import { AssessmentPanels } from '@/components/raven/assessment';
import { Notes } from '@/components/raven/notes';
import { Profile } from '@/components/raven/profile';
import { dateLabel, OLink, RiskBadge } from '@/components/raven/shared';
import { useONavigate } from '@/hooks/use-organization-navigate';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';
import { useOrganizationStore } from '@/stores/organization-store';
import { DocumentsSection } from './documents-section';
import { KeyMembersAccordion } from './key-members-accordion';
import { ProfileDecisionSection } from './profile-decision-section';
import { SignalsSection } from './signals-section';
import { WorkflowEventsLog } from './workflow-events-log';

export const ComplianceReviewView = ({
  report,
}: {
  report: RouterOutputs['report']['getReport'];
}) => {
  useLocale();

  const generation = trpc.report.getReportGeneration.useQuery({ reportId: report._id }, {});
  const navigate = useONavigate();
  const role = useOrganizationStore((state) => state.currentOrganization.currentOrganizationRole);
  const create = trpc.report.createReport.useMutation({
    onSuccess: (created) => navigate({ to: `/reports/create/${created._id}` }),
    onError: (cause) => toast.error(errorMessage(AppError.parse(cause))),
  });
  return (
    <div>
      <nav aria-label={t('auth.reportNavigation')} className="mb-5 flex justify-end gap-8">
        {report.previousId && (
          <OLink to={`/reports/${report.previousId}`}>{t('auth.previousReport')}</OLink>
        )}
        {report.nextId && <OLink to={`/reports/${report.nextId}`}>{t('auth.nextReport')}</OLink>}
      </nav>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="raven-label">
            {report.reportType} {t('workspace.report')}
          </span>
          <h1>
            {report.reportType} {t('auth.reportFor')} {report.reportName}
          </h1>
          <p className="text-sm">
            {t('auth.generatedByRavenOn')}{' '}
            {dateLabel(
              report.generatedAt ??
                generation.data?.events.findLast((event) => event.stage === 'finished')?.at ??
                report.createdAt,
              true,
            )}
            .
          </p>
        </div>
        <Button
          disabled={create.isPending || role === UserRole.ORG_VIEWER}
          onClick={() =>
            create.mutate({
              reportType: report.reportType,
              clientId: report.clientId,
              sourceReportId: report._id,
            })
          }
          variant="outline"
        >
          {t('auth.generateNewReport')}{' '}
        </Button>
      </div>
      {report.clientId && (
        <OLink to={`/clients/${report.clientId}`}>{t('auth.viewClientProfile')}</OLink>
      )}
      {report.documents?.some((document) =>
        document.verification?.issues.some((issue) => issue.field === 'expiryDate'),
      ) && (
        <p className="raven-panel mb-5 text-amber-700" role="status">
          {t('reports.documentExpiryWarning')}
        </p>
      )}
      <section className="raven-section">
        <span className="raven-label">{t('landing.profileDecision')}</span>
        <div className="grid gap-4 lg:grid-cols-2">
          <Profile report={report} />
          <div className="space-y-4">
            <ProfileDecisionSection reportDetail={report} />
            <div className="raven-panel">
              <h2>
                {report.assessment?.profileSummary
                  ? t('auth.profileSummary')
                  : t('landing.riskSummary')}
              </h2>
              <div className="my-4">
                <RiskBadge level={report.riskLevel} />
                {report.assessment?.provisional && (
                  <span className="raven-muted ms-3">{t('auth.provisionalAssessment')}</span>
                )}
              </div>
              <p className="whitespace-pre-line text-sm">
                {report.assessment?.profileSummary ||
                  generation.data?.progress.find((progress) =>
                    generation.data?.subjects.some(
                      (subject) => subject.id === progress.subjectId && subject.kind !== 'member',
                    ),
                  )?.summary ||
                  report.riskSummary ||
                  t('extra.noSummaryIsAvailable')}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {report.signals
                  ?.filter(
                    (signal) =>
                      signal.resolution !== SignalResolution.DISMISSED &&
                      (signal.match === 'matching' || signal.identityConfirmed) &&
                      signal.classification === 'ADVERSE',
                  )
                  .slice(0, 5)
                  .map((signal) => (
                    <span className="raven-risk" data-risk={signal.severity} key={signal._id}>
                      {signal.title}
                    </span>
                  ))}
              </div>
              <p className="raven-muted mt-4">{report.riskSummary}</p>
            </div>
          </div>
        </div>
      </section>
      <AssessmentPanels assessment={report.assessment} />
      <section className="raven-section">
        <span className="raven-label">{t('landing.detectedSignals')}</span>
        <h3 className="mb-4">{t('reports.findings')}</h3>
        <SignalsSection reportId={report._id} signals={report.signals ?? []} />
      </section>
      {report.reportType === ReportType.KYB && (
        <KeyMembersAccordion
          assessment={report.assessment}
          keyMembers={report.keyMembers ?? []}
          reportId={report._id}
        />
      )}
      <section className="raven-section" id="documents">
        <span className="raven-label">{t('reports.documents')}</span>
        <DocumentsSection documents={report.documents} />
      </section>
      <Notes targetId={report._id} targetType="report" />
      {
        <details className="raven-panel mt-6">
          <summary className="cursor-pointer text-sm">
            {t('auth.researchCoverageSourcesProcessingHistory')}{' '}
          </summary>
          <div className="mt-5">
            <WorkflowEventsLog reportId={report._id} />
          </div>
        </details>
      }
    </div>
  );
};
